/**
 * Client-side reconnect hints used to reduce race conditions during join/reconnect flows.
 *
 * Storage responsibilities:
 * - `pendingTargetGameId` (sessionStorage): short-lived explicit intent ("join this game now").
 * - `lastActiveGameId` (localStorage): longer-lived same-device fallback for reload/continue.
 *
 * Server DB state remains the source of truth. These values are hints only.
 */
import {
  getReconnectLastActiveGameIdKey,
  getReconnectPendingTargetGameIdKey,
  STORAGE_KEY_ALIASES,
} from '~/app/storageKeys';
import { gameIdSchema } from '~/app/storageSchemas';
import { getCurrentStorageUserScopeId } from '~/app/storageScope';
import { safeParseStoredString } from '~/app/storageValidation';

/** Guard for browser-only storage access (SSR-safe). */
const canUseStorage = (): boolean => typeof window !== 'undefined';

/** Persist the last successfully active player game on this device/browser. */
const resolveLastActiveGameIdKey = (userScopeId?: string): string =>
  getReconnectLastActiveGameIdKey(userScopeId ?? getCurrentStorageUserScopeId());

const resolvePendingTargetGameIdKey = (): string => getReconnectPendingTargetGameIdKey();

export const setLastActiveGameId = (gameId: string | undefined, userScopeId?: string): void => {
  if (!canUseStorage() || !gameId) return;
  const key = resolveLastActiveGameIdKey(userScopeId);
  window.localStorage.setItem(key, gameId);
};

/** Read the last active game id for same-device reconnect fallback. */
export const getLastActiveGameId = (userScopeId?: string): string | undefined => {
  if (!canUseStorage()) return undefined;
  const canonicalKey = resolveLastActiveGameIdKey(userScopeId);
  const canonical = safeParseStoredString(window.localStorage.getItem(canonicalKey), gameIdSchema);
  if (canonical.ok) return canonical.data;
  if (canonical.reason !== 'missing') window.localStorage.removeItem(canonicalKey);

  // Legacy fallback: read old key names and migrate into canonical account-scoped key.
  // TODO: [storage-migration-cleanup] Drop legacy read path when most active clients migrated.
  for (const legacyKey of STORAGE_KEY_ALIASES.reconnectLastActiveGameId) {
    const legacy = safeParseStoredString(window.localStorage.getItem(legacyKey), gameIdSchema);
    if (legacy.ok) {
      window.localStorage.setItem(canonicalKey, legacy.data);
      return legacy.data;
    }
    if (legacy.reason !== 'missing') window.localStorage.removeItem(legacyKey);
  }
  return undefined;
};

/** Persist explicit immediate intent for the current tab/session (e.g. user just clicked "join"). */
export const setPendingTargetGameId = (gameId: string | undefined): void => {
  if (!canUseStorage() || !gameId) return;
  window.sessionStorage.setItem(resolvePendingTargetGameIdKey(), gameId);
};

/**
 * Read and clear the pending target id.
 * One-shot consumption prevents stale explicit intent from leaking into later reconnects.
 */
export const consumePendingTargetGameId = (): string | undefined => {
  if (!canUseStorage()) return undefined;
  const canonicalKey = resolvePendingTargetGameIdKey();
  const canonical = safeParseStoredString(
    window.sessionStorage.getItem(canonicalKey),
    gameIdSchema,
  );
  if (canonical.ok) {
    window.sessionStorage.removeItem(canonicalKey);
    return canonical.data;
  }
  if (canonical.reason !== 'missing') window.sessionStorage.removeItem(canonicalKey);

  // Legacy fallback: accept old session keys during rollout.
  // TODO: [storage-migration-cleanup] Remove legacy lookup/removal after migration window.
  for (const legacyKey of STORAGE_KEY_ALIASES.reconnectPendingTargetGameId) {
    const legacy = safeParseStoredString(window.sessionStorage.getItem(legacyKey), gameIdSchema);
    if (legacy.ok) {
      window.sessionStorage.removeItem(legacyKey);
      return legacy.data;
    }
    if (legacy.reason !== 'missing') window.sessionStorage.removeItem(legacyKey);
  }

  for (const legacyKey of STORAGE_KEY_ALIASES.reconnectPendingTargetGameId) {
    window.sessionStorage.removeItem(legacyKey);
  }
  return undefined;
};

/**
 * Resolve reconnect target with deterministic priority:
 * 1) explicit pending target for current transition, then
 * 2) last active game on this device.
 *
 * If both are absent, caller should rely on server fallback (latest active game for user).
 */
export const getPreferredReconnectGameId = (userScopeId?: string): string | undefined => {
  const pending = consumePendingTargetGameId();
  const lastActive = getLastActiveGameId(userScopeId);
  return pending ?? lastActive;
};
